<script setup lang="ts">
import { toast } from "vue-sonner";
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";
import { buildTemplateContext, useTemplating } from "#bb/composables/useTemplating";

const props = defineProps<{ id: string }>();

const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id) || {});
const features = inject<{
  editor?: boolean;
  resourceDefinitionPath?: string | null;
}>("render-features", { editor: false });

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "PDF report download editor",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the PDF report download",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "PDF Report Download",
    icon: "lucide:file-down",
  },
});

const logger = useLogger("PdfReportDownload");
const report = inject<unknown | null>("report", null);
const templateContext = computed(() => buildTemplateContext(report));
const { applyTemplateObject } = useTemplating(templateContext, { logger });
const templated = computed(() => applyTemplateObject({
  title: definitionData.value.title || "Download your report",
  description: definitionData.value.description || "",
  buttonText: definitionData.value.buttonText || "Download PDF",
  automationPath: definitionData.value.automationPath || "",
  reportPath: definitionData.value.reportPath || "",
  fileName: definitionData.value.fileName || "",
}));

const { busy, errorMessage, execute } = usePdfReportDownload({
  automationPath: () => templated.value.automationPath,
  reportPath: () => templated.value.reportPath,
  fileName: () => templated.value.fileName,
  resourceDefinitionPath: () => features.resourceDefinitionPath,
});

async function download() {
  if (features.editor) return;

  try {
    await execute();
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") return;
    toast.error(errorMessage.value || "The PDF report could not be downloaded.");
  }
}
</script>

<template>
  <section
    :class="cn(
      'print:hidden rounded-lg border bg-card p-5 text-card-foreground',
      '@min-[640px]:flex @min-[640px]:items-center @min-[640px]:justify-between @min-[640px]:gap-6',
      $attrs.class ?? '',
    )"
  >
    <div class="flex min-w-0 items-start gap-3.5">
      <div class="flex size-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Icon name="lucide:file-text" class="size-5" />
      </div>
      <div class="min-w-0">
        <h2 class="text-base font-semibold">{{ templated.title }}</h2>
        <p
          v-if="templated.description"
          class="mt-1 max-w-2xl text-sm text-muted-foreground"
        >
          {{ templated.description }}
        </p>
        <p
          v-if="errorMessage"
          class="mt-2 text-sm text-destructive"
          role="alert"
        >
          {{ errorMessage }}
        </p>
      </div>
    </div>

    <Button
      type="button"
      class="mt-4 w-full shrink-0 @min-[640px]:mt-0 @min-[640px]:w-auto"
      :disabled="busy || features.editor"
      aria-live="polite"
      @click="download"
    >
      <Icon
        :name="busy ? 'lucide:loader-circle' : (definitionData.icon || 'lucide:download')"
        :class="cn('size-4', busy && 'animate-spin')"
      />
      {{ busy ? "Preparing PDF..." : templated.buttonText }}
    </Button>
  </section>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";

export default {
  componentId: ComponentDefinitionIds.PDF_REPORT_DOWNLOAD,
  icon: "lucide:file-down",
  editor: () => import("#bb/components/editor/content/pdf-report-download.vue"),
  type: "PDF Report Download",
  description:
    "Runs an optional automation and downloads the generated PDF report.",
  default: {
    title: "Download your report",
    description: "Generate the latest version, then save the PDF to your device.",
    buttonText: "Download PDF",
    icon: "lucide:download",
    automationPath: "",
    reportPath: "/data/<userId>/reports/report.pdf",
    fileName: "",
  },
  categories: ["Content"],
  order: 13041,
};
</script>
